import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CompletedActivityCard from "../common/CompletedActivityCard";
import Spacer from "../Spacer";
import TaskListEmptyStateCard from "../tasks/TaskListEmptyStateCard";
import Pulmonology from "../icons/Pulmonology";
import RespiratoryRate from "../icons/RespiratoryRate";
import VoiceSelectionIcon from "../icons/VoiceSelectionIcon";
import StackedLineChartIcon from "../icons/StackedLineChartIcon";
import DirectionsWalkIcon from "../icons/DirectionsWalkIcon";
import { getRecordingTaskIdForVariant } from "../../hooks/useProtocolTaskBoard";
import { getTaskBoardCompletionSummary } from "../../utils/tasks/taskBoardSummary";
import { fetchRegionalQuestionnaireHistory } from "../../utils/backend/pdxBackendClient";
import { QUESTIONNAIRES } from "../../utils/constants";
import { RECORDING_MODES } from "../../utils/recordingModes";
import { useRegionalMeasurementResults } from "../../hooks/results/useRegionalMeasurementResults";
import {
  RESULT_MODE_CONFIG,
  getResultModeConfigForVariant,
} from "../../utils/results/resultModeConfig";
import { buildResultPath } from "../../utils/results/resultNavigation";
import {
  formatDateParts,
  getLocalDayKeyMs,
  parseTimestampFromRecordingId,
} from "../../utils/results/recordingIdTime";
import { collapseSixMinuteWalkHistoryRows } from "../../utils/results/sixMinuteWalk";

const DAY_MS = 24 * 60 * 60 * 1000;
const HISTORY_DAYS = 7;
const HOME_HISTORY_LIMIT = 20;
const HOME_HISTORY_MEASUREMENTS_PER_VARIANT = 3;
const QUESTIONNAIRE_META = Object.fromEntries(
  QUESTIONNAIRES.map(({ slug, labelKey }) => [slug, { labelKey }]),
);

function parseHistoryDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === "string" && value.trim()) {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime())
      ? date
      : null;
  }

  if (typeof value?.seconds === "number") {
    const date = new Date(value.seconds * 1000);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  return null;
}

function normalizeQuestionnaireEntry(entry, locale) {
  const questionnaireId =
    typeof entry?.questionnaireId === "string"
      ? entry.questionnaireId.trim().toLowerCase()
      : "";

  if (!questionnaireId) return null;

  const timestamp =
    entry?.takenAt ||
    entry?.submittedAt ||
    entry?.clientSubmittedAt ||
    entry?.createdAt ||
    entry?.updatedAt ||
    null;
  const date = parseHistoryDate(timestamp);

  if (!date) return null;

  const meta = QUESTIONNAIRE_META[questionnaireId];
  const { time, date: formattedDate } = formatDateParts(date, locale);

  return {
    kind: "questionnaire",
    id:
      typeof entry?.docId === "string" && entry.docId.trim()
        ? entry.docId
        : `${questionnaireId}:${date.toISOString()}`,
    questionnaireId,
    labelKey: meta?.labelKey ?? null,
    fallbackLabel: questionnaireId,
    score: typeof entry?.score === "number" ? entry.score : null,
    timestampMs: date.getTime(),
    timestamp: date.toISOString(),
    dayKey: getLocalDayKeyMs(date.getTime(), locale),
    time,
    date: formattedDate,
  };
}

function useHomeQuestionnaireHistory(uid, refreshKey, locale) {
  const [state, setState] = useState({
    loading: false,
    error: null,
    entries: [],
  });

  useEffect(() => {
    if (!uid) {
      setState({ loading: false, error: null, entries: [] });
      return undefined;
    }

    let cancelled = false;
    setState((previousState) => ({
      ...previousState,
      loading: true,
      error: null,
    }));

    fetchRegionalQuestionnaireHistory({ limit: HOME_HISTORY_LIMIT })
      .then((response) => {
        if (cancelled) return;

        const entries = Array.isArray(response?.questionnaires)
          ? response.questionnaires
              .map((entry) => normalizeQuestionnaireEntry(entry, locale))
              .filter(Boolean)
          : [];

        setState({ loading: false, error: null, entries });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({ loading: false, error, entries: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [locale, refreshKey, uid]);

  return state;
}

function makeRecordingItem(row, config, activityType, locale) {
  const rowConfig = config || getResultModeConfigForVariant(row?.variant);
  const timestampDate =
    parseHistoryDate(row?.timestamp) ||
    parseHistoryDate(row?.completedAt) ||
    parseHistoryDate(row?.createdAt) ||
    parseHistoryDate(row?.takenAt) ||
    parseHistoryDate(row?.submittedAt) ||
    parseTimestampFromRecordingId(row?.id);
  const timestampMs = timestampDate?.getTime();
  const dayKey = Number.isFinite(timestampMs)
    ? getLocalDayKeyMs(timestampMs, locale)
    : null;
  const variant = row?.variant || rowConfig?.variant;

  if (
    !variant ||
    !rowConfig ||
    !Number.isFinite(timestampMs) ||
    dayKey == null
  ) {
    return null;
  }

  return {
    ...row,
    kind: "recording",
    activityType,
    id: `${variant}:${row.id}`,
    docId: row.id,
    variant,
    context: rowConfig.context,
    tab: rowConfig.tab,
    timestampMs,
    timestamp: timestampDate.toISOString(),
    time: row?.time || formatDateParts(timestampDate, locale).time,
    date: row?.date || formatDateParts(timestampDate, locale).date,
    dayKey,
    to: buildResultPath({
      variant,
      docid: row.id,
      context: rowConfig.context,
      tab: rowConfig.tab,
    }),
  };
}

function getRecordingLabel(item, t) {
  if (item.activityType === "spirometry") {
    return t("results.modes.spirometry", "Spirometry");
  }

  if (item.activityType === "manualAudio") {
    return t("results.modes.manualAudio", "Audio");
  }

  if (item.activityType === "postExertion") {
    return t("results.modes.postExertion", "Exertion");
  }

  if (item.activityType === "sixMinuteWalk") {
    return t("results.modes.sixMinuteWalk", "6-Minute Walk");
  }

  return t("results.modes.cardiopulmonary", "Vitals");
}

function formatDayLabel(dayKey, todayKey, locale, t) {
  if (dayKey === todayKey) return t("home.history.today", "Today");
  if (dayKey === todayKey - DAY_MS) {
    return t("home.history.yesterday", "Yesterday");
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "short",
  }).format(new Date(dayKey));
}

function DateSpacer({ label }) {
  return (
    <div className="w-full flex items-center gap-3 py-1">
      <Spacer />
      <span className="shrink-0 muted-text text-xs font-semibold uppercase tracking-normal">
        {label}
      </span>
      <Spacer />
    </div>
  );
}

function FeedEndSpacer({ label }) {
  return (
    <div className="w-full flex items-center gap-3 py-1">
      <Spacer />
      <span className="shrink-0 muted-text text-xs font-semibold tracking-normal">
        {label}
      </span>
      <Spacer />
    </div>
  );
}

function getActivityLabel(item, t) {
  if (item.kind === "questionnaire") {
    return item.labelKey
      ? t(item.labelKey, item.fallbackLabel)
      : item.fallbackLabel;
  }

  return getRecordingLabel(item, t);
}

// Mirrors getRecordingLabel's branches so a measurement card shows the same
// icon as its recording-mode button (rendered at the card's icon size/colour
// via CompletedActivityCard's element-icon slot).
function getRecordingModeIcon(item) {
  if (item.activityType === "spirometry") return <Pulmonology />;
  if (item.activityType === "manualAudio") return <VoiceSelectionIcon />;
  if (item.activityType === "postExertion") return <StackedLineChartIcon />;
  if (item.activityType === "sixMinuteWalk") return <DirectionsWalkIcon />;

  return <RespiratoryRate />;
}

function getActivityTypeForVariant(variant) {
  if (variant === RESULT_MODE_CONFIG.spirometry.variant) return "spirometry";
  if (variant === RESULT_MODE_CONFIG.manualAudio.variant) return "manualAudio";
  if (variant === RESULT_MODE_CONFIG.postExertion.variant)
    return "postExertion";
  if (variant === RESULT_MODE_CONFIG.sixMinuteWalk.variant)
    return "sixMinuteWalk";
  return "cardiopulmonary";
}

function getHistoryItemTaskKey(item) {
  if (item?.kind === "questionnaire" && item?.questionnaireId) {
    return `questionnaire:${item.questionnaireId}`;
  }

  if (item?.kind === "recording") {
    const taskId = getRecordingTaskIdForVariant(item?.variant);

    if (taskId) {
      return `recording:${taskId}`;
    }
  }

  return null;
}

/**
 * Collapses an already time-sorted list into runs of consecutive same-kind
 * activities. A burst of e.g. three questionnaire completions becomes one run
 * so it renders under a single heading instead of repeating the title per card.
 */
function groupConsecutiveActivities(items) {
  const runs = [];

  items.forEach((item) => {
    const currentRun = runs[runs.length - 1];

    if (currentRun && currentRun[0].kind === item.kind) {
      currentRun.push(item);
    } else {
      runs.push([item]);
    }
  });

  return runs;
}

function ActivityGroup({ items, t }) {
  const [first] = items;
  const isQuestionnaire = first.kind === "questionnaire";
  const heading = isQuestionnaire
    ? t("home.history.questionnaireCompleted", "Questionnaire completed")
    : t("home.history.measurementCompleted", "Measurement completed");

  return (
    <div className="w-full flex flex-col gap-2">
      <h3 className="text-lg font-bold text-typography">{heading}</h3>
      {items.map((item) => (
        <CompletedActivityCard
          key={`${item.kind}:${item.id}`}
          label={getActivityLabel(item, t)}
          meta={item.time}
          icon={
            item.kind === "questionnaire"
              ? "assignment_turned_in"
              : getRecordingModeIcon(item)
          }
          to={item.to}
        />
      ))}
    </div>
  );
}

function buildGroups(items, todayKey, showTodaySupplement) {
  const groupsByDay = new Map();

  items.forEach((item) => {
    if (!groupsByDay.has(item.dayKey)) {
      groupsByDay.set(item.dayKey, []);
    }
    groupsByDay.get(item.dayKey).push(item);
  });

  if (showTodaySupplement && todayKey != null && !groupsByDay.has(todayKey)) {
    groupsByDay.set(todayKey, []);
  }

  return Array.from(groupsByDay.entries())
    .map(([dayKey, groupItems]) => ({
      dayKey,
      items: groupItems.sort(
        (left, right) => right.timestampMs - left.timestampMs,
      ),
    }))
    .sort((left, right) => right.dayKey - left.dayKey);
}

function HomeActivityHistory({
  uid,
  isPermitted,
  loadingModes = false,
  historyRefreshKey = 0,
  taskBoard = null,
  todaySupplement = null,
  showCompletedTaskCelebration = true,
}) {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.resolvedLanguage || i18n.language || "en-GB";
  const activeUid = loadingModes ? null : uid;
  const modeIsPermitted =
    typeof isPermitted === "function" ? isPermitted : () => false;
  const todayKey = getLocalDayKeyMs(Date.now(), locale);
  const earliestDayKey =
    todayKey == null ? null : todayKey - (HISTORY_DAYS - 1) * DAY_MS;

  const measurementVariants = useMemo(() => {
    const variants = [];

    if (modeIsPermitted(RECORDING_MODES.SPIROMETRY)) {
      variants.push(RESULT_MODE_CONFIG.spirometry.variant);
    }
    if (modeIsPermitted(RECORDING_MODES.TIDAL)) {
      variants.push(RESULT_MODE_CONFIG.cardiopulmonary.variant);
    }
    if (modeIsPermitted(RECORDING_MODES.MAUDIO)) {
      variants.push(RESULT_MODE_CONFIG.manualAudio.variant);
    }
    if (modeIsPermitted(RECORDING_MODES.POSTEXERTION)) {
      variants.push(RESULT_MODE_CONFIG.postExertion.variant);
    }
    if (modeIsPermitted(RECORDING_MODES.SIX_MINUTE_WALK)) {
      variants.push(RESULT_MODE_CONFIG.sixMinuteWalk.variant);
    }

    return variants;
  }, [modeIsPermitted]);
  const measurementResults = useRegionalMeasurementResults({
    uid: activeUid,
    variants: measurementVariants,
    pageSize: HOME_HISTORY_LIMIT,
    perVariantLimit: HOME_HISTORY_MEASUREMENTS_PER_VARIANT,
    recentWithinMs: HISTORY_DAYS * DAY_MS,
    languageFallback: locale,
    refreshKey: historyRefreshKey,
  });
  const questionnaireHistory = useHomeQuestionnaireHistory(
    activeUid,
    historyRefreshKey,
    locale,
  );

  const historyItems = useMemo(() => {
    const recordingItems = collapseSixMinuteWalkHistoryRows(
      measurementResults.rows || [],
    )
      .map((row) =>
        makeRecordingItem(
          row,
          getResultModeConfigForVariant(row?.variant),
          getActivityTypeForVariant(row?.variant),
          locale,
        ),
      )
      .filter(Boolean);

    return [...recordingItems, ...(questionnaireHistory.entries || [])]
      .filter((item) => {
        if (earliestDayKey == null || todayKey == null) return true;
        return item.dayKey >= earliestDayKey && item.dayKey <= todayKey;
      })
      .sort((left, right) => right.timestampMs - left.timestampMs);
  }, [
    earliestDayKey,
    locale,
    measurementResults.rows,
    questionnaireHistory.entries,
    todayKey,
  ]);

  const historyIsFetching =
    measurementResults.loading || questionnaireHistory.loading;
  const historyError = measurementResults.error || questionnaireHistory.error;
  const taskBoardSummary = useMemo(
    () => getTaskBoardCompletionSummary(taskBoard),
    [taskBoard],
  );
  const lastCompletedTaskToday = useMemo(() => {
    const assignedTaskKeys = new Set(
      (Array.isArray(taskBoard?.tasks) ? taskBoard.tasks : [])
        .map((task) =>
          typeof task?.id === "string" && task.id
            ? task.id
            : task?.kind && task?.taskId
              ? `${task.kind}:${task.taskId}`
              : null,
        )
        .filter(Boolean),
    );

    if (!assignedTaskKeys.size || todayKey == null) {
      return null;
    }

    return (
      historyItems.find(
        (item) =>
          item.dayKey === todayKey &&
          assignedTaskKeys.has(getHistoryItemTaskKey(item)),
      ) || null
    );
  }, [historyItems, taskBoard?.tasks, todayKey]);
  const shouldShowCompletedTaskCelebration =
    showCompletedTaskCelebration &&
    !historyIsFetching &&
    !historyError &&
    taskBoard?.state === "ready" &&
    taskBoardSummary.total > 0 &&
    taskBoardSummary.outstanding === 0 &&
    lastCompletedTaskToday != null;
  const showTodaySupplement = Boolean(todaySupplement);
  const groups = useMemo(
    () => buildGroups(historyItems, todayKey, showTodaySupplement),
    [historyItems, showTodaySupplement, todayKey],
  );
  const showEmpty = !historyIsFetching && !historyError && groups.length === 0;

  return (
    <section
      className="w-full min-h-fit flex-1 flex flex-col gap-2 bg-greyscale-1 p-4 rounded-t-3xl"
      aria-label={t("home.history.title", "History")}
    >
      <div className="w-full flex flex-col gap-3">
        {shouldShowCompletedTaskCelebration ? (
          <TaskListEmptyStateCard
            icon="list_alt_check"
            message={t(
              "taskList.sections.due.empty",
              "Great job! You've completed all the tasks due today.",
            )}
            grouped
            iconClassName="text-primary"
            textClassName="text-typography"
          />
        ) : null}

        {groups.map((group) => {
          const groupIsToday = group.dayKey === todayKey;
          const shouldShowDate = !groupIsToday;

          return (
            <div key={group.dayKey} className="w-full flex flex-col gap-3">
              {shouldShowDate ? (
                <DateSpacer
                  label={formatDayLabel(group.dayKey, todayKey, locale, t)}
                />
              ) : null}

              {groupConsecutiveActivities(group.items).map((run) => (
                <ActivityGroup
                  key={`${run[0].kind}:${run[0].id}`}
                  items={run}
                  t={t}
                />
              ))}

              {groupIsToday && todaySupplement ? todaySupplement : null}
            </div>
          );
        })}

        {historyError ? (
          <p className="muted-text text-center">
            {t(
              "home.history.unavailable",
              "We couldn't load your recent activity right now.",
            )}
          </p>
        ) : null}

        {showEmpty ? (
          <p className="muted-text text-center">
            {t("home.history.empty", "No activity this week.")}
          </p>
        ) : null}
      </div>

      {!historyIsFetching ? (
        <FeedEndSpacer label={t("home.history.endOfFeed", "end of feed")} />
      ) : null}

      <Link to="/results" className="small-outlined-button w-full text-center">
        {t("home.history.seeResults", "See Results")}
      </Link>
    </section>
  );
}

export default memo(HomeActivityHistory);
